import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import User from "@sonic/Assets/Icons/User";
import CustomAccountMenu from "@sonic/CustomComponents/CustomMobiusComponenets/Menu/CustomAccountMenu";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
}

const mapStateToProps = (state: ApplicationState) => ({
    isSignInPage: getCurrentPage(state).type === "SignInPage",
    userName: state.context.session.userName,
    myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    currentUserIsGuest: getCurrentUserIsGuest(state),
    isPunchOutSession: getIsPunchOutSession(state),
    currentLocation: getLocation(state),
    displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
});

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            color: currentColor;
            display: inline-block;
            text-align: center;

            button {
                color: currentColor;
            }
        `,
    },
    titleClickable: {
        css: css`
            margin-left: 0;
            padding-right: 10px;
            display: inline-block;
            text-align: center;
        `,
    },
    titleIcon: {
        color: "currentColor",
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            margin: auto;
            border: 1px solid currentColor;
            border-radius: 50%;
            width: 35px;
            height: 35px;

            svg {
                height: 18px;
                color: currentColor;
            }
        `,
        size: 18,
    },
    titleTypography: {
        size: 10,
        css: css`
            text-transform: uppercase;
            white-space: nowrap;
            display: block;
            font-weight: 500;
            color: currentColor;
            max-width: 40px;
        `,
        ellipsis: true,
        transform: "none",
        color: "currentColor",
    },
    signOutClickable: {
        css: css`
            flex: 0 0 auto;
            margin-left: 30px;
            text-transform: capitalize;
        `,
    },
};

const iconMap = {
    customerAccount: User,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: keyof typeof iconMap;
        [fields.includeAccountMenu]: boolean;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;
const styles = headerSignInStyles;
export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
}: Props) => {
    const showIcon = fields.visibilityState === "both" || fields.visibilityState === "icon";
    const showLabel = fields.visibilityState === "both" || fields.visibilityState === "label";
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);

    const icon = iconMap[(showIcon && fields.icon) || "none"];

    const onSignInHandler = (e: React.MouseEvent) => {
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
            } else {
                const returnUrl = currentLocation.pathname + currentLocation.search;
                if (returnUrl === "/") {
                    history.push(`${signInUrl}`);
                } else {
                    history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
                }
            }
            e.preventDefault();
        }
    };

    const onSignOutHandler = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    let displayName = userName;

    if (userName && userName.length > 12) {
        displayName = userName.substring(0, 9).concat("...");
    }

    let signInStatusText = translate("Sign In");
    if (userName && !currentUserIsGuest) {
        signInStatusText = displayName;
    } else if (isSignInPage) {
        signInStatusText = translate("Signing in...");
    }

    const myAccountClickHander = () => {
        setTimeout(() => setIsMenuOpen(false), 100);
        history.push("/MyAccount");
    };

    return (
        <StyledWrapper {...styles.signOutWrapper}>
            {fields.includeAccountMenu && userName && !currentUserIsGuest && (
                <CustomAccountMenu
                    isOpen={isMenuOpen || undefined}
                    descriptionId="accountMenu"
                    menuItems={
                        myAccountPageLink?.children?.filter(
                            pageLink => !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink),
                        ) ?? []
                    }
                    displayChangeCustomerLink={displayChangeCustomerLink}
                    maxDepth={1}
                    menuTrigger={
                        <Clickable
                            {...styles.titleClickable}
                            data-test-selector="header_signIn"
                            onClick={myAccountClickHander}
                        >
                            {showIcon && <Icon {...styles.titleIcon} size={30} src={icon} />}
                            {showLabel && (
                                <Typography
                                    title={displayName}
                                    {...styles.titleTypography}
                                    data-test-selector="header_userName"
                                >
                                    {displayName}
                                </Typography>
                            )}
                        </Clickable>
                    }
                    cssOverrides={{
                        wrapper: css`
                            flex: 0 1 auto;
                            min-width: 0;
                        `,
                    }}
                    data-test-selector="header_accountMenu"
                    signOut={signOut}
                    cancelPunchOut={cancelPunchOut}
                    isPunchOutSession={isPunchOutSession}
                />
            )}
            {(!fields.includeAccountMenu || !userName || (userName && currentUserIsGuest)) && (
                <Clickable
                    {...styles.titleClickable}
                    onClick={onSignInHandler}
                    href={!signInUrl ? "/MyAccount/SignIn" : signInUrl}
                    data-test-selector="header_signIn"
                >
                    {showIcon && <Icon {...styles.titleIcon} size={30} src={icon} />}
                    {showLabel && (
                        <Typography title={signInStatusText} {...styles.titleTypography}>
                            {signInStatusText}
                        </Typography>
                    )}
                </Clickable>
            )}
            {/* {userName && !currentUserIsGuest && (
                <Clickable
                    {...styles.signOutClickable}
                    onClick={onSignOutHandler}
                    data-test-selector="headerSignOutLink"
                >
                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                </Clickable>
            )} */}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(HeaderSignIn)),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "customer Account",
                        value: "customerAccount",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
